/* Real Stigg integration for Tokenwise (OPSS-55).
 *
 * Mirrors studio-web's saas-stigg.service: wrap the app in Stigg's React SDK
 * provider (public client key), "harden" the client by binding it to the
 * signed-in customer with a token minted by identity, then read plan/trial
 * state client-side straight from Stigg.
 *
 * NOTE: `@stigg/react-sdk` must be installed (`npm i` after `npm run npmrc`)
 * for this file to typecheck/build. The exact SDK method/enum names are marked
 * with TODO(OPSS-55) — confirm against the installed SDK version. */

import {
  StiggProvider as StiggProviderSdk,
  Paywall as StiggPaywallSdk,
  Checkout as StiggCheckoutSdk,
  useStiggContext,
  type BillingPeriod,
  type OnPlanSelectedCallbackFn,
} from "@stigg/react-sdk";
import { useCallback, useEffect, useState } from "react";
import type {
  ComponentType,
  FunctionComponent,
  PropsWithChildren,
} from "react";

import { useCurrentUser } from "../../identity/client";
import { STIGG_ENABLED_FLAG, useFlags } from "../flags";
import { isActiveStiggCustomerId, isAi21User } from "./stigg.eligibility";
import {
  STIGG_CLIENT_KEY,
  STIGG_CUSTOMER_TOKEN_URL,
  STIGG_THEME,
  TOKENWISE_FEATURE_ID,
  TOKENWISE_PLAN_ID,
  TOKENWISE_PRODUCT_ID,
} from "./stigg.config";
import type {
  GetCustomerTokenResponse,
  IStiggService,
  TokenwiseCheckoutProps,
  TokenwisePaywallProps,
  TokenwisePlanState,
} from "./stigg.types";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Bind the SDK client to the signed-in customer using a signed token from
 *  identity, so per-customer entitlement reads are authorized. Returns whether
 *  hardening has settled (success or a graceful skip). */
function useStiggHardening(): boolean {
  const { stigg } = useStiggContext();
  const { stiggCustomerId } = useCurrentUser();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Seam (OPSS-55): identity must expose `stiggCustomerId` on /identity/user.
    // Until it does, skip hardening gracefully (reads fall back to no access).
    if (!stiggCustomerId || !STIGG_CUSTOMER_TOKEN_URL) {
      setReady(true);
      return;
    }

    (async () => {
      try {
        const res = await fetch(STIGG_CUSTOMER_TOKEN_URL, {
          credentials: "include",
        });
        if (res.ok) {
          const customerToken = (await res.json()) as GetCustomerTokenResponse;
          if (!cancelled && customerToken) {
            stigg.setCustomerId(stiggCustomerId, customerToken);
          }
        }
      } catch (error) {
        console.error("[Stigg] customer hardening failed", error);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stigg, stiggCustomerId]);

  return ready;
}

const StiggProvider: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => (
  <StiggProviderSdk apiKey={STIGG_CLIENT_KEY ?? ""} theme={STIGG_THEME}>
    <Hardened>{children}</Hardened>
  </StiggProviderSdk>
);

const Hardened: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  useStiggHardening();
  return <>{children}</>;
};

const useTokenwisePlan = (): TokenwisePlanState => {
  const { stigg } = useStiggContext();
  const { stiggCustomerId, email } = useCurrentUser();
  const { ready: flagsReady, isEnabled } = useFlags();
  const stiggEnabled = isEnabled(STIGG_ENABLED_FLAG);
  const [state, setState] = useState<TokenwisePlanState>({
    isLoading: true,
    hasSubscription: false,
    isEntitled: false,
    isTrial: false,
  });

  useEffect(() => {
    let cancelled = false;

    // Show nothing (and don't touch Stigg) for users Tokenwise billing doesn't apply to:
    //  - AI21 employees are always pro (rule);
    //  - users without an active Stigg customer aren't on Tokenwise (studio-web's
    //    useIsStiggUser policy).
    if (isAi21User(email) || !isActiveStiggCustomerId(stiggCustomerId)) {
      setState({
        isLoading: false,
        hasSubscription: false,
        isEntitled: true,
        isTrial: false,
      });
      return;
    }

    // Wait for the stigg-enabled flag before deciding — avoids a show→hide flash.
    if (!flagsReady) {
      setState((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    // Not managed by Stigg (flag off / unknown) ⇒ show nothing, mirroring the
    // backend, which treats stigg-enabled=off as "not gated" (fallback False).
    if (!stiggEnabled) {
      setState({
        isLoading: false,
        hasSubscription: false,
        isEntitled: true,
        isTrial: false,
      });
      return;
    }

    (async () => {
      try {
        // TODO(OPSS-55): confirm SDK method names (getBooleanEntitlement /
        // getActiveSubscriptions) and Subscription shape against the pinned SDK.
        const entitlement = await stigg.getBooleanEntitlement({
          featureId: TOKENWISE_FEATURE_ID,
        });
        const subscriptions = await stigg.getActiveSubscriptions();
        const tokenwiseSub = subscriptions.find(
          (subscription) => subscription.plan?.id === TOKENWISE_PLAN_ID,
        );

        const isTrial = tokenwiseSub?.status === "IN_TRIAL";
        const trialEndsAt = tokenwiseSub?.trialEndDate
          ? new Date(tokenwiseSub.trialEndDate).getTime()
          : undefined;
        const trialDaysLeft =
          isTrial && trialEndsAt !== undefined
            ? Math.max(
                0,
                Math.ceil((trialEndsAt - new Date().getTime()) / MS_PER_DAY),
              )
            : undefined;

        if (!cancelled) {
          setState({
            isLoading: false,
            hasSubscription: Boolean(tokenwiseSub),
            isEntitled: Boolean(entitlement?.hasAccess),
            isTrial,
            trialDaysLeft,
          });
        }
      } catch (error) {
        console.error("[Stigg] failed to read Tokenwise plan state", error);
        // Fail open in the UI: no subscription context ⇒ show nothing (don't nag).
        if (!cancelled)
          setState({
            isLoading: false,
            hasSubscription: false,
            isEntitled: true,
            isTrial: false,
          });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stigg, stiggCustomerId, email, flagsReady, stiggEnabled]);

  return state;
};

const Paywall: ComponentType<TokenwisePaywallProps> = ({ onPlanSelected }) => {
  const handlePlanSelected = useCallback<OnPlanSelectedCallbackFn>(
    ({ plan, selectedBillingPeriod }) => {
      // Hand the selection to the app, which opens the checkout modal.
      onPlanSelected?.(plan.id, selectedBillingPeriod);
    },
    [onPlanSelected],
  );
  // Scope the paywall to the Intelligent Gateway product, else Stigg renders the
  // default (Studio) product's plans.
  return (
    <StiggPaywallSdk
      productId={TOKENWISE_PRODUCT_ID}
      onPlanSelected={handlePlanSelected}
    />
  );
};

const Checkout: ComponentType<TokenwiseCheckoutProps> = ({
  planId,
  billingPeriod,
  onCompleted,
}) => (
  <StiggCheckoutSdk
    planId={planId}
    preferredBillingPeriod={billingPeriod as BillingPeriod | undefined}
    // Plan + add-ons are already chosen from the paywall; go straight to payment.
    skipCheckoutSteps={["PLAN", "ADDONS"]}
    onCheckoutCompleted={async () => onCompleted()}
  />
);

export class SaasStiggService implements IStiggService {
  StiggProvider = StiggProvider;
  useTokenwisePlan = useTokenwisePlan;
  Paywall = Paywall;
  Checkout = Checkout;
}
